// @systeme: interface
// L'onglet Notes : ce qu'on garde sans action (le code du portail, la taille des
// chaussures, la liste des médicaments), rangé par catégorie. Une note se lit d'un coup
// d'œil, s'édite au doigt, avec trois conventions d'écriture (puces, numéros, gras).
import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Pencil } from 'lucide-react';
import type { CategorieElement, Element } from '@foyer/shared';
import { CategoriesElement } from '@foyer/shared';
import { Chargement } from '../composants/Chargement';
import { useMessages } from '../composants/Messages';
import { TexteRiche } from '../composants/TexteRiche';
import { api } from '../lib/api';
import { couleurs, tailles } from '../theme/theme';

export const NOMS_CATEGORIES: Record<CategorieElement, string> = {
  poubelles: 'Poubelles',
  sante: 'Santé',
  administratif: 'Papiers',
  scolaire: 'École',
  maison: 'Maison',
  social: 'Famille, amis',
  courses: 'Courses',
  travail: 'Travail',
  finances: 'Finances',
  autre: 'Autre',
};

type Filtre = 'toutes' | CategorieElement | 'sans';

interface Brouillon {
  id?: string;
  titre: string;
  detail: string;
  categorie: CategorieElement | '';
  prive: boolean;
  sante: boolean;
}

const VIDE: Brouillon = { titre: '', detail: '', categorie: '', prive: false, sante: false };

export function Notes() {
  const { dire, direErreur } = useMessages();
  const [notes, setNotes] = useState<Element[] | undefined>();
  const [filtre, setFiltre] = useState<Filtre>('toutes');
  const [brouillon, setBrouillon] = useState<Brouillon | undefined>();
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    try {
      setNotes((await api.elements.notes()).notes);
    } catch (e) {
      direErreur(e);
    }
  }, [direErreur]);

  useEffect(() => {
    api.elements
      .notes()
      .then((v) => setNotes(v.notes))
      .catch(direErreur);
  }, [direErreur]);

  async function enregistrer() {
    if (!brouillon || brouillon.titre.trim() === '') return;
    setEnCours(true);
    try {
      const communs = {
        titre: brouillon.titre.trim(),
        detail: brouillon.detail.trim() === '' ? null : brouillon.detail,
        categorie: brouillon.categorie === '' ? null : brouillon.categorie,
        visibilite: brouillon.prive ? ('prive' as const) : ('espace' as const),
        sensibilite: brouillon.sante ? ('sante' as const) : ('normale' as const),
      };
      if (brouillon.id) await api.elements.modifier(brouillon.id, communs);
      else
        await api.elements.creer({
          type: 'note',
          titre: communs.titre,
          detail: communs.detail ?? undefined,
          categorie: communs.categorie ?? undefined,
          jour: null,
          visibilite: communs.visibilite,
          sensibilite: communs.sensibilite,
        });
      dire('C’est noté.');
      setBrouillon(undefined);
      await charger();
    } catch (e) {
      direErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  async function supprimer() {
    if (!brouillon?.id) return;
    setEnCours(true);
    try {
      await api.elements.supprimer(brouillon.id);
      dire('Supprimée.');
      setBrouillon(undefined);
      await charger();
    } catch (e) {
      direErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  function ouvrir(note?: Element) {
    setBrouillon(
      note
        ? {
            id: note.id,
            titre: note.titre,
            detail: note.detail ?? '',
            categorie: note.categorie ?? '',
            prive: note.visibilite === 'prive',
            sante: note.sensibilite === 'sante',
          }
        : VIDE,
    );
  }

  if (!notes) return <Chargement />;

  const categoriesPresentes = CategoriesElement.filter((c) => notes.some((n) => n.categorie === c));
  const sansCategorie = notes.some((n) => n.categorie === null);
  const visibles = notes.filter((n) =>
    filtre === 'toutes' ? true : filtre === 'sans' ? n.categorie === null : n.categorie === filtre,
  );

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3, pb: 10 }}>
      <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
        Notes
      </Typography>

      {(categoriesPresentes.length > 1 || (categoriesPresentes.length > 0 && sansCategorie)) && (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
          <Chip
            label="Toutes"
            color={filtre === 'toutes' ? 'primary' : 'default'}
            variant={filtre === 'toutes' ? 'filled' : 'outlined'}
            onClick={() => setFiltre('toutes')}
          />
          {categoriesPresentes.map((c) => (
            <Chip
              key={c}
              label={NOMS_CATEGORIES[c]}
              color={filtre === c ? 'primary' : 'default'}
              variant={filtre === c ? 'filled' : 'outlined'}
              onClick={() => setFiltre(c)}
            />
          ))}
          {sansCategorie && (
            <Chip
              label="Sans catégorie"
              color={filtre === 'sans' ? 'primary' : 'default'}
              variant={filtre === 'sans' ? 'filled' : 'outlined'}
              onClick={() => setFiltre('sans')}
            />
          )}
        </Stack>
      )}

      {visibles.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Le code du portail, la pointure de Théo, le nom de la nounou… Ce qu’on garde sans avoir
          rien à faire.
        </Typography>
      ) : (
        visibles.map((n) => (
          <Paper
            key={n.id}
            variant="outlined"
            onClick={() => ouvrir(n)}
            role="button"
            tabIndex={0}
            sx={{ p: 2, mb: 1.5, cursor: 'pointer', borderColor: 'divider' }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
              <Typography variant="h3" component="h2">
                {n.titre}
              </Typography>
              {n.categorie && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {NOMS_CATEGORIES[n.categorie]}
                </Typography>
              )}
            </Stack>
            {n.detail && (
              <Box sx={{ mt: 1 }}>
                <TexteRiche texte={n.detail} />
              </Box>
            )}
            {(n.visibilite === 'prive' || n.sensibilite === 'sante') && (
              <Typography variant="body2" color="text.secondary">
                {[
                  n.visibilite === 'prive' ? 'rien que pour toi' : null,
                  n.sensibilite === 'sante' ? 'santé' : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            )}
          </Paper>
        ))
      )}

      <Fab
        color="secondary"
        variant="extended"
        aria-label="Nouvelle note"
        onClick={() => ouvrir()}
        sx={{
          position: 'fixed',
          bottom: `calc(${tailles.navigation + 16}px + env(safe-area-inset-bottom))`,
          right: tailles.marge,
          px: 3,
        }}
      >
        <Pencil size={20} style={{ marginRight: 8 }} />
        Noter
      </Fab>

      <Drawer
        anchor="bottom"
        open={brouillon !== undefined}
        onClose={() => setBrouillon(undefined)}
      >
        {brouillon && (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              void enregistrer();
            }}
            sx={{ px: `${tailles.marge}px`, pt: 2, pb: 'calc(24px + env(safe-area-inset-bottom))' }}
          >
            <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
              {brouillon.id ? 'Cette note' : 'Une note'}
            </Typography>
            <Stack spacing={2}>
              <TextField
                value={brouillon.titre}
                onChange={(e) => setBrouillon({ ...brouillon, titre: e.target.value })}
                placeholder="Code du portail"
                autoFocus
                inputProps={{ maxLength: 200, 'aria-label': 'Titre' }}
              />
              <TextField
                value={brouillon.detail}
                onChange={(e) => setBrouillon({ ...brouillon, detail: e.target.value })}
                placeholder={'4521\n- sonner deux fois\n- **attendre** le bip'}
                multiline
                minRows={4}
                inputProps={{ maxLength: 5000, 'aria-label': 'Contenu' }}
                helperText="Une ligne qui commence par « - » fait une puce, « 1. » une étape, **un mot** en gras."
              />
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() =>
                    setBrouillon({
                      ...brouillon,
                      detail: `${brouillon.detail}${brouillon.detail === '' || brouillon.detail.endsWith('\n') ? '' : '\n'}- `,
                    })
                  }
                >
                  • Puce
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() =>
                    setBrouillon({
                      ...brouillon,
                      detail: `${brouillon.detail}${brouillon.detail === '' || brouillon.detail.endsWith('\n') ? '' : '\n'}1. `,
                    })
                  }
                >
                  1. Étape
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setBrouillon({ ...brouillon, detail: `${brouillon.detail}****` })}
                >
                  Gras
                </Button>
              </Stack>
              <TextField
                select
                label="Catégorie"
                value={brouillon.categorie}
                onChange={(e) =>
                  setBrouillon({
                    ...brouillon,
                    categorie: CategoriesElement.find((c) => c === e.target.value) ?? '',
                  })
                }
              >
                <MenuItem value="">Aucune</MenuItem>
                {CategoriesElement.map((c) => (
                  <MenuItem key={c} value={c}>
                    {NOMS_CATEGORIES[c]}
                  </MenuItem>
                ))}
              </TextField>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <FormControlLabel
                  control={
                    <Switch
                      checked={brouillon.prive}
                      onChange={(e) => setBrouillon({ ...brouillon, prive: e.target.checked })}
                    />
                  }
                  label="Rien que pour moi"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={brouillon.sante}
                      onChange={(e) => setBrouillon({ ...brouillon, sante: e.target.checked })}
                    />
                  }
                  label="C’est de la santé"
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={enCours || brouillon.titre.trim() === ''}
                  sx={{ flex: 1 }}
                >
                  C’est noté
                </Button>
                {brouillon.id && (
                  <Button color="error" disabled={enCours} onClick={() => void supprimer()}>
                    Supprimer
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        )}
      </Drawer>
      <Box sx={{ height: 0, bgcolor: couleurs.creme }} />
    </Box>
  );
}
